/**
 * Reine Regeln des Hinweises „Diese Unterlage weicht vom Stand der
 * Planungsphase ab" (Plan Planungsphase, Stufe 6.1 — Rückfrage statt
 * Automatik). Die Komponente `AbweichungsHinweis.tsx` zeigt nur an und ruft
 * auf; was sie zeigt und ob eine Begründung reicht, steht hier und ist ohne
 * DOM geprüft.
 *
 * Die Typen sind STRUKTURELL gehalten (`HinweisStand`), nicht an den
 * Änderungsdienst gebunden: der Hinweis braucht von einem Stand nur Unterlage,
 * Offen-Flag, Kennung und die Zeilen — alles Weitere (Entscheidung, Ausgabe)
 * geht ihn nichts an. So bleibt die Regel prüfbar, ohne den Dienst zu laden.
 */
import { BEGRUENDUNG_MINDESTLAENGE } from '../../services/unterlagenAusgabenService';

/** Eine Zeile des Vergleichs — Merkmal, alter und neuer Wert. */
export interface AbweichungZeile {
    /** Pfad im Auszug (`fragen[PL-C-11].status`) — nur für die Kurzfassung, die Listen zusammenzieht. */
    pfad?: string;
    label: string;
    altText: string;
    neuText: string;
}

/** Das, was der Hinweis von einem Stand des Änderungsdienstes wissen muss. */
export interface HinweisStand {
    /** Die betroffene Unterlage — der Hinweis zeigt nur Stände SEINER Unterlage. */
    unterlage: string;
    /** Noch nicht entschieden (oder „Später"): nur offene Stände werden gezeigt. */
    offen: boolean;
    /** Stabile Kennung des Vergleichs — Schlüssel fürs Ausblenden in der Sitzung. */
    hash: string;
    /** Die Ausgabe, gegen die verglichen wird — ihre Id gehört in die Kennung des Entscheids. */
    ausgabe: { id: string };
    abweichungen: readonly AbweichungZeile[];
}

/**
 * Kennung eines Entscheids: Ausgabe-Id UND Hash. Der Hash allein hängt nur
 * an Pfad und Werten — dieselbe Änderung ergibt für zwei Unterlagen denselben
 * Hash, und ein Riegel nur über den Hash sperrte die falsche Frage mit.
 */
export const entscheidKennung = (stand: Pick<HinweisStand, 'hash' | 'ausgabe'>): string =>
    `${(stand.ausgabe?.id || '').trim()}:${stand.hash}`;

/**
 * Was der Hinweis zeigt.
 *
 *   nichts          — noch nicht gelesen, nichts offen, oder alles in der
 *                     Sitzung weggelegt bzw. gerade entschieden (und kein
 *                     Lesefehler).
 *   nicht_geprueft  — der Dienst konnte nicht alles lesen: der Abgleich
 *                     KONNTE NICHT GEPRÜFT werden. Ein dezenter Satz, damit
 *                     „kein Hinweis" nie wie „alles geprüft" aussieht — aber
 *                     auch keine Stände und keine Knöpfe, denn aus Ersatzwerten
 *                     gerechnete Abweichungen wären keine (Hausregel: Fehler
 *                     sieht nie wie Entwarnung aus, aber auch nicht wie eine
 *                     Abweichung).
 *   offen           — mindestens ein offener Stand dieser Unterlage, alle
 *                     Quellen gelesen.
 */
export type HinweisAnzeige<T extends HinweisStand> =
    | { art: 'nichts' }
    | { art: 'nicht_geprueft'; text: string }
    | { art: 'offen'; staende: T[] };

export interface HinweisEingabe<T extends HinweisStand> {
    /** Erst `true`, wenn der Dienst die Stände GENAU dieses Projekts hat. */
    bereit: boolean;
    lesefehler: string | null;
    staende: readonly T[];
    unterlage: string;
    /** In dieser Sitzung mit „Später" weggelegte Kennungen (`hash`) — gespeichert wird davon nichts. */
    ausgeblendet: ReadonlySet<string>;
    /**
     * In dieser Sitzung erfolgreich entschiedene Kennungen (`entscheidKennung`):
     * sofort weg, auch bevor das Neuladen den Stand entfernt — sonst ließe
     * sich derselbe Entscheid ein zweites Mal speichern.
     */
    erledigt?: ReadonlySet<string>;
}

/** Der Satz zu einem Lesefehler — dezent, kein Fehlerton, aber auch keine Entwarnung. */
export const lesefehlerSatz = (lesefehler: string): string =>
    `Ob diese Unterlage vom Stand der Planungsphase abweicht, konnte nicht geprüft werden: ${lesefehler.trim()} `
    + 'Bis zum erfolgreichen Lesen ist kein Entscheid möglich.';

/**
 * Entscheidet, was der Hinweis zeigt. Vor `bereit` NICHTS — `[]` heißt dort
 * „noch nicht gelesen", nicht „nichts offen" (Muster `useProjektSteckbrief`).
 * Mit Lesefehler NIE Stände: was der Dienst dann liefert, stammt aus
 * Ersatzwerten und darf nicht zur Entscheidung angeboten werden.
 */
export const hinweisAnzeige = <T extends HinweisStand>(e: HinweisEingabe<T>): HinweisAnzeige<T> => {
    if (!e.bereit) return { art: 'nichts' };
    const lesefehler = (e.lesefehler || '').trim() || null;
    if (lesefehler) return { art: 'nicht_geprueft', text: lesefehlerSatz(lesefehler) };
    const erledigt = e.erledigt || new Set<string>();
    const offen = e.staende.filter(s =>
        s.unterlage === e.unterlage && s.offen && !e.ausgeblendet.has(s.hash) && !erledigt.has(entscheidKennung(s)));
    if (offen.length > 0) return { art: 'offen', staende: offen };
    return { art: 'nichts' };
};

/**
 * Kopfzeile eines Standes: „Versendete Fassung vom 17.10.2026 — 2 Änderungen".
 * Der Anlass sagt, WAS verglichen wird (das Blatt, das hinausging bzw.
 * freigegeben wurde); die Fassungskennung selbst ist technisch und bleibt weg.
 */
export const fassungSatz = (
    ausgabe: { anlass: 'freigabe' | 'versand'; erstellt_am: string },
    anzahl: number,
): string => {
    const was = ausgabe.anlass === 'versand' ? 'Versendete Fassung' : 'Freigegebene Fassung';
    const d = new Date(ausgabe.erstellt_am || '');
    const wann = Number.isNaN(d.getTime()) ? '' : ` vom ${d.toLocaleDateString('de-DE')}`;
    const wieViele = anzahl === 1 ? '1 Änderung' : `${anzahl} Änderungen`;
    return `${was}${wann} — ${wieViele}`;
};

const OHNE_WERT = '—';

/** Eine Zeile in ihren Teilen, so wie sie dasteht — Grundlage von `abweichungText` und der langen Werte. */
export interface AbweichungTeile {
    label: string;
    alt: string;
    neu: string;
}

/** Beschriftung ohne Ränder (sonst „Angabe"), fehlende Werte als Strich. */
export const abweichungTeile = (z: AbweichungZeile): AbweichungTeile => ({
    label: (z.label || '').trim() || 'Angabe',
    alt: (z.altText || '').trim() || OHNE_WERT,
    neu: (z.neuText || '').trim() || OHNE_WERT,
});

/** „Merkmal Kran: nein → ja" — ein fehlender Wert steht als Strich, nie als leere Stelle. */
export const abweichungText = (z: AbweichungZeile): string => {
    const t = abweichungTeile(z);
    return `${t.label}: ${t.alt} → ${t.neu}`;
};

/**
 * Ab so vielen Zeichen steht ein Wert in der aufgeklappten Liste erst mit seinem Anfang und einem
 * Knopf „mehr zeigen" da (je Wert). Ein Tooltip hilft auf Tablet und Handy nicht — dort gibt es kein
 * Überfahren mit der Maus; kürzere Werte stehen ganz da und brechen um.
 */
export const WERT_KURZ_GRENZE = 200;

export const wertIstLang = (text: string, grenze: number = WERT_KURZ_GRENZE): boolean => (text || '').length > grenze;

/**
 * Anfang eines langen Wertes: bis zur Grenze, möglichst am letzten Leerraum im hinteren Drittel,
 * nie mitten in einem Ersatzzeichenpaar (Emoji), mit „…". Kurze Werte bleiben, wie sie sind.
 */
export const wertAnfang = (text: string, grenze: number = WERT_KURZ_GRENZE): string => {
    const t = text || '';
    if (!wertIstLang(t, grenze)) return t;
    let ende = grenze;
    for (let i = grenze; i >= Math.ceil(grenze * 2 / 3); i--) {
        if (/\s/.test(t.charAt(i))) { ende = i; break; }
    }
    const code = t.charCodeAt(ende - 1);
    if (code >= 0xd800 && code <= 0xdbff) ende -= 1;
    return `${t.slice(0, ende).trimEnd()} …`;
};

/**
 * Bis zu so vielen Zeilen steht die Liste gleich offen; bei mehr startet sie
 * zugeklappt mit der Kurzfassung (`abweichungUeberblick`) — sonst schiebt
 * eine frisch befüllte Planungsphase (143 Zeilen) die Unterlage aus dem Bild.
 */
export const AUFKLAPP_GRENZE = 5;

export const startetAufgeklappt = (anzahl: number): boolean => anzahl <= AUFKLAPP_GRENZE;

const LISTE_MEHRZAHL: Record<string, string> = { fragen: 'Fragen', unterlagen: 'Unterlagen' };

/** Höchstens so viele Gruppen nennt die Kurzfassung; mehr → die ersten drei und „N weitere". */
const UEBERBLICK_HOECHSTENS = 4;

/**
 * Kurzfassung für die zugeklappte Liste: „Ausführungszeitraum Beginn,
 * Baubeschreibung, 141 Fragen". Elemente einer Liste (`fragen[PL-C-11]…`)
 * zählen je Element einmal und stehen als Anzahl — ein einzelnes mit seiner
 * Beschriftung; Einzelangaben stehen mit ihrer Beschriftung. Reihenfolge wie
 * die Zeilen (der Dienst sortiert nach Pfad).
 */
export const abweichungUeberblick = (zeilen: readonly AbweichungZeile[]): string => {
    const gruppen = new Map<string, { label: string; liste: string | null; elemente: Set<string> }>();
    zeilen.forEach(z => {
        const label = (z.label || '').trim() || 'Angabe';
        const element = (z.pfad || '').match(/^(\w+)\[([^\]]+)\]/);
        const schluessel = element ? `liste:${element[1]}` : `wert:${(z.pfad || '').trim() || label}`;
        const g = gruppen.get(schluessel) ?? { label, liste: element ? element[1] : null, elemente: new Set<string>() };
        if (element) g.elemente.add(element[2]);
        gruppen.set(schluessel, g);
    });
    const teile = [...gruppen.values()].map(g =>
        (g.liste && g.elemente.size > 1 ? `${g.elemente.size} ${LISTE_MEHRZAHL[g.liste] || g.liste}` : g.label));
    if (teile.length <= UEBERBLICK_HOECHSTENS) return teile.join(', ');
    return `${teile.slice(0, UEBERBLICK_HOECHSTENS - 1).join(', ')} und ${teile.length - UEBERBLICK_HOECHSTENS + 1} weitere`;
};

/**
 * Die Mindestlänge kommt aus dem Dienst (dort auch der CHECK der Tabelle,
 * `BEGRUENDUNG_MINDESTLAENGE`), damit Hinweis, Dialog und Datenbank dieselbe
 * Grenze haben; die Höchstlänge ist eine Grenze des Textfelds.
 */
export { BEGRUENDUNG_MINDESTLAENGE };
export const BEGRUENDUNG_HOECHSTLAENGE = 500;

/**
 * Prüft die Pflichtbegründung zu „Nicht erheblich" — Grund als Satz oder
 * `null`, wenn sie reicht. (Grund-oder-null statt `{ ok: boolean }`: das
 * Projekt übersetzt ohne `strictNullChecks`, Muster `pruefeAblageAuftrag`.)
 *
 * Die Begründung ist der Nachweis, dass der Koordinator die Änderung bewertet
 * hat (BaustellV: Anpassung bei erheblichen Änderungen). Leer oder nur aus
 * Leerzeichen gilt nicht; „ok" auch nicht.
 */
export const pruefeBegruendung = (text: string): string | null => {
    const rein = (text || '').replace(/\s+/g, ' ').trim();
    if (!rein) return 'Bitte kurz begründen, warum die Änderung für diese Unterlage nicht erheblich ist.';
    if (rein.length < BEGRUENDUNG_MINDESTLAENGE) {
        return 'Die Begründung ist zu kurz — ein Satz, der später nachvollziehbar ist.';
    }
    if (rein.length > BEGRUENDUNG_HOECHSTLAENGE) {
        return `Die Begründung ist zu lang (höchstens ${BEGRUENDUNG_HOECHSTLAENGE} Zeichen).`;
    }
    return null;
};

/** Die Begründung, wie sie gespeichert wird: Mehrfach-Leerzeichen und Ränder weg. */
export const begruendungBereinigt = (text: string): string =>
    (text || '').replace(/\s+/g, ' ').trim();

/**
 * Der Fehlersatz aus der Antwort von `entscheide` — oder `null` bei Erfolg.
 *
 * Der Änderungsdienst wirft nicht (Hausregel der Dienste rund um die Ablage);
 * sein Ergebnis kann eine Zeichenkette (der Grund), ein Ergebnisobjekt mit
 * `status: 'fehler'` bzw. `fehler` oder nichts sein. Alle drei Lesarten laufen
 * hier zusammen, damit die Komponente keinen Fehler verschluckt, egal in
 * welcher Form er kommt.
 */
export const entscheidFehler = (ergebnis: unknown): string | null => {
    if (ergebnis === null || ergebnis === undefined || ergebnis === true) return null;
    if (ergebnis === false) return 'Der Entscheid wurde nicht gespeichert.';
    if (typeof ergebnis === 'string') return ergebnis.trim() || null;
    if (typeof ergebnis === 'object') {
        const o = ergebnis as { status?: unknown; fehler?: unknown };
        const satz = typeof o.fehler === 'string' ? o.fehler.trim() : '';
        if (satz) return satz;
        if (o.status === 'fehler') return 'Der Entscheid wurde nicht gespeichert.';
    }
    return null;
};
